import React, { useEffect, useRef } from "react";

const MathJaxRenderer = ({ content, className }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    // Debug the content received
    if (content) {
      console.log("MathJaxRenderer - Content length:", content.length);
      console.log(
        "MathJaxRenderer - Content preview:",
        content.substring(0, 200)
      );
    } else {
      console.log("MathJaxRenderer - No content provided");
    }

    // Only proceed if we have content
    if (content && containerRef.current) {
      try {
        // Set the HTML content
        containerRef.current.innerHTML = content;
        console.log("MathJaxRenderer - Content set to container");

        // Process the math expressions with MathJax if available
        if (window.MathJax) {
          console.log(
            "MathJaxRenderer - MathJax is available, typesetting now"
          );
          window.MathJax.typeset([containerRef.current]);
        } else {
          console.warn(
            "MathJaxRenderer - MathJax is not available on the page"
          );
        }
      } catch (error) {
        console.error("MathJaxRenderer - Error rendering content:", error);
        // Fallback to a simpler approach if there's an error
        containerRef.current.textContent = content;
      }
    }
  }, [content]);

  // Hide raw content section completely
  const renderContentDirectly = () => {
    return null; // This completely removes the raw content section
  };

  // Apply CSS to hide any existing raw content containers
  useEffect(() => {
    // Create a style element
    const style = document.createElement("style");
    // Target both specific and general classes to ensure all instances are hidden
    style.innerHTML = `
      /* Global CSS selectors to hide raw content */
      .mt-3.p-2.border-top.raw-content-container, 
      .mt-3.p-2.border-top,
      [class*="raw-content-container"],
      .raw-content-container,
      div.border.p-3.bg-light.rounded pre {
        display: none !important;
      }
      
      /* Additional aggressive selectors for upload-question page */
      h5 + .border.p-3.bg-light.rounded,
      h6 + .border.p-3.bg-light.rounded,
      [class*="Tab"][title*="HTML Source"],
      [class*="tab-pane"][aria-label*="raw"],
      div.border.p-3.bg-light.rounded:has(pre),
      [id*="raw"],
      .nav-tabs [role="tab"]:nth-child(2),
      .tab-content .tab-pane:nth-child(2) {
        display: none !important;
      }
    `;

    // Add DOM-based hiding for elements that can't be targeted with CSS alone
    const hideRawContent = () => {
      // More aggressive approach to find and hide raw content sections

      // Check if we're on the upload-question page
      const isUploadPage = window.location.pathname.includes("upload-question");
      console.log("MathJaxRenderer - Current page:", window.location.pathname);

      // 1. Look for headings with "Raw content" text
      const headings = document.querySelectorAll("h5, h6");
      headings.forEach((heading) => {
        if (heading.textContent.includes("Raw content")) {
          console.log("Found Raw content heading:", heading);
          // Hide this heading
          heading.style.display = "none";

          // Also hide its parent or next element which likely contains the raw content
          if (heading.parentElement) {
            const parent = heading.parentElement;

            // Option 1: If heading is inside a container with the raw content
            if (
              parent.classList.contains("border") ||
              parent.classList.contains("bg-light") ||
              parent.classList.contains("rounded")
            ) {
              parent.style.display = "none";
            }

            // Option 2: Hide sibling elements that likely contain the raw content
            const siblings = parent.children;
            for (let i = 0; i < siblings.length; i++) {
              if (siblings[i] === heading) {
                // Found our heading, now hide the next element if it exists
                if (siblings[i + 1]) {
                  siblings[i + 1].style.display = "none";
                }
                break;
              }
            }
          }
        }
      });

      // 2. Find any elements with text containing "Raw content (if rendering fails)"
      const allElements = document.querySelectorAll(
        "div, p, span, h5, h6, pre, code, label"
      );
      allElements.forEach((element) => {
        if (
          element.textContent &&
          element.textContent.includes("Raw content")
        ) {
          console.log("Found element with Raw content text:", element);
          element.style.display = "none";

          // Also hide the next element which likely contains the raw content
          let nextElement = element.nextElementSibling;
          if (nextElement) {
            nextElement.style.display = "none";
          }

          // Also hide the parent if it might be a container
          let parent = element.parentElement;
          if (
            parent &&
            (parent.classList.contains("border") ||
              parent.classList.contains("rounded") ||
              parent.classList.contains("bg-light") ||
              parent.tagName === "TAB" ||
              parent.tagName === "TABS")
          ) {
            parent.style.display = "none";
          }
        }
      });

      // 3. Special handling for upload-question page
      if (isUploadPage) {
        console.log(
          "MathJaxRenderer - Special handling for upload-question page"
        );

        // Hide tab navigation elements that might contain "HTML Source" or "Raw"
        const tabElements = document.querySelectorAll('.nav-tabs [role="tab"]');
        tabElements.forEach((tab, index) => {
          if (
            index > 0 || // Hide all tabs except the first one
            (tab.textContent &&
              (tab.textContent.includes("HTML") ||
                tab.textContent.includes("Source") ||
                tab.textContent.includes("Raw")))
          ) {
            tab.style.display = "none";
          }
        });

        // Hide tab content panes except the first one
        const tabPanes = document.querySelectorAll(".tab-content .tab-pane");
        tabPanes.forEach((pane, index) => {
          if (index > 0) {
            // Hide all except the first tab pane
            pane.style.display = "none";
          }
        });

        // Hide any pre elements that might contain raw HTML
        const preElements = document.querySelectorAll("pre");
        preElements.forEach((pre) => {
          pre.style.display = "none";
          if (pre.parentElement) {
            pre.parentElement.style.display = "none";
          }
        });
      }
    };

    // Run once immediately
    hideRawContent();

    // And also set up a MutationObserver to handle dynamically added content
    const observer = new MutationObserver(() => {
      hideRawContent();
    });

    // Start observing the document with the configured parameters
    observer.observe(document.body, { childList: true, subtree: true });

    // Append the style to the document head
    document.head.appendChild(style);

    // Clean up function to remove the style and disconnect observer when the component unmounts
    return () => {
      document.head.removeChild(style);
      observer.disconnect();
    };
  }, []);

  return (
    <div className={className || ""}>
      <div ref={containerRef} />
      {renderContentDirectly()}
    </div>
  );
};

export default MathJaxRenderer;
